import React from "react";
import { IoMdClose } from "react-icons/io";
import { FaFlask, FaBuilding } from "react-icons/fa";

/**
 * QuickBooksEnvModal - Modal for selecting QuickBooks environment (Sandbox/Production)
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {function} onSelect - Callback with selected env type ("SANDBOX" or "PRODUCTION")
 */
const QuickBooksEnvModal = ({ isOpen, onClose, onSelect }) => {
    if (!isOpen) return null;

    const handleSelect = (envType) => {
        onSelect(envType);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative z-10 bg-zinc-900 rounded-xl shadow-2xl max-w-md w-[90%] border border-zinc-700">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700">
                    <div className="flex items-center gap-3">
                        <img
                            src="assets/icons/quickbooks.png"
                            alt="QuickBooks"
                            className="w-8 h-8"
                        />
                        <h2 className="text-lg font-semibold text-white">
                            Connect QuickBooks
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                    >
                        <IoMdClose className="text-zinc-400 hover:text-white" size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5">
                    <p className="mb-5 text-sm text-zinc-400">
                        Select the QuickBooks environment you want to connect to:
                    </p>

                    <div className="space-y-3">
                        {/* Sandbox Option */}
                        <button
                            onClick={() => handleSelect("SANDBOX")}
                            className="w-full p-4 text-left transition-all border rounded-lg bg-zinc-800 border-zinc-700 hover:border-amber-500 hover:bg-zinc-750 group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-amber-500/10 text-amber-500 group-hover:bg-amber-500/20">
                                    <FaFlask size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-white">Sandbox</h3>
                                    <p className="text-xs text-zinc-400">
                                        For testing and development purposes
                                    </p>
                                </div>
                            </div>
                        </button>

                        {/* Production Option */}
                        <button
                            onClick={() => handleSelect("PRODUCTION")}
                            className="w-full p-4 text-left transition-all border rounded-lg bg-zinc-800 border-zinc-700 hover:border-green-500 hover:bg-zinc-750 group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-green-500/10 text-green-500 group-hover:bg-green-500/20">
                                    <FaBuilding size={24} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-white">Production</h3>
                                    <p className="text-xs text-zinc-400">
                                        Connect to your live QuickBooks data
                                    </p>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-zinc-700">
                    <p className="text-xs text-center text-zinc-500">
                        Make sure to select the environment that matches your QuickBooks account
                    </p>
                </div>
            </div>
        </div>
    );
};

export default QuickBooksEnvModal;
